import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardHeader } from '@/src/modules/components/Header';
import {
  FullScreenCalendar,
  type CalendarData,
} from '@/src/modules/components/ui/fullscreen-calendar';
import { UpcomingEventsCard } from '@/src/modules/components/calendar/UpcomingEventsCard';
import {
  getCalendarEvents,
  type ApiCalendarEvent,
} from '@/src/infrastructure/lib/api';

/**
 * Group the calendar projection into the shape FullScreenCalendar expects.
 * The grid widget keys events by day, so we bucket them client-side.
 */
function toCalendarData(events: ApiCalendarEvent[]): CalendarData[] {
  const byDay = new Map<string, CalendarData>();

  events.forEach((event, index) => {
    const date = new Date(event.startDatetime);
    if (Number.isNaN(date.getTime())) return;

    const key = date.toDateString();
    if (!byDay.has(key)) byDay.set(key, { day: date, events: [] });

    byDay.get(key)!.events.push({
      id: index + 1,
      name: event.title,
      time: date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      }),
      datetime: event.startDatetime,
    });
  });

  return Array.from(byDay.values());
}

export function Calendar() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<ApiCalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Hits the /calendar/events projection — driven by invitation domain events
  // on the backend. We pass an explicit 15-month window (3 months back → 12
  // months ahead) so the calendar grid can navigate freely without re-fetching,
  // and so events are never silently dropped by the narrow server-side default
  // window (current month + next month only) that caused the production bug.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');

    const now = new Date();
    const startDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 3, 1))
      .toISOString()
      .split('T')[0];
    const endDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 13, 0))
      .toISOString()
      .split('T')[0];

    getCalendarEvents({ startDate, endDate })
      .then((data) => {
        if (!active) return;
        setEvents(data);
      })
      .catch((err) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : 'Failed to load calendar.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const calendarData = useMemo(() => toCalendarData(events), [events]);
  const isEmpty = !loading && !error && events.length === 0;

  return (
    <div className="flex-1 flex flex-col min-h-screen bg-surface-lowest">
      <DashboardHeader
        title="Calendar"
        description="Every published invitation, laid out by event date."
      />

      <div className="flex-1 flex flex-col gap-6 p-4 md:p-8">
        <UpcomingEventsCard />

        {loading ? (
          <CalendarSkeleton />
        ) : error ? (
          <ErrorPanel message={error} onRetry={() => window.location.reload()} />
        ) : isEmpty ? (
          <EmptyPanel onCreate={() => navigate('/wizard')} />
        ) : (
          <FullScreenCalendar
            data={calendarData}
            onNewEvent={() => navigate('/wizard')}
          />
        )}
      </div>
    </div>
  );
}

function CalendarSkeleton() {
  return (
    <div className="flex-1 grid grid-cols-7 gap-2 animate-pulse">
      {Array.from({ length: 35 }).map((_, i) => (
        <div key={i} className="h-24 rounded-lg bg-surface-low" />
      ))}
    </div>
  );
}

function EmptyPanel({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center gap-4 py-16">
      <div className="text-on-surface-variant text-lg">
        No events scheduled yet.
      </div>
      <p className="text-on-surface-variant max-w-md">
        Once you publish your first invitation, it will automatically appear
        here on the day of the event.
      </p>
      <button
        onClick={onCreate}
        className="px-4 py-2 rounded-md bg-primary text-white font-medium hover:opacity-90"
      >
        Create an invitation
      </button>
    </div>
  );
}

function ErrorPanel({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 py-16">
      <div className="text-status-failed font-medium">Unable to load calendar</div>
      <p className="text-on-surface-variant max-w-md">{message}</p>
      <button
        onClick={onRetry}
        className="px-4 py-2 rounded-md border border-outline-variant text-on-surface hover:bg-surface-low"
      >
        Try again
      </button>
    </div>
  );
}
